// 성과 측정 카드
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Insta from "../../images/insta_logo.png";
import Facebook from "../../images/facebook_logo.png";
import Youtube from "../../images/youtube_logo.png";
import tiktok from "../../images/tictok_logo.png";

const PLATFORM_ICONS = {
  instagram: Insta,
  facebook: Facebook,
  tiktok: tiktok,
  youtube: Youtube,
};

const BRAND = {
  blue: "#0054C0",
  textPrimary: "#172940",
  textSecondary: "#5D6979",
  cardBg: "white",
};

export default function ContentsList({ data, defaultExpanded = false }) {
  const navigate = useNavigate();

  const STATUS_STYLE = {
    CONFIRMED: {
      text: "검수완료",
      color: "#27C6DA",
      bg: "#DEF1FB",
    },
    REJECTED: {
      text: "수정요청",
      color: "#DB5851",
      bg: "#F1E6EE",
    },
    REVIEW: {
      text: "검수중",
      color: "#1560C4",
      bg: "#D9E6F9",
    },
    DEFAULT: {
      text: "검수대기",
      color: "#667181",
      bg: "#E3E8F2",
    },
  };

  const isCps = String(data?.billingModel ?? "").toUpperCase() === "CPS";

  const formatScheduleDate = (v) => {
    if (v == null || v === "") return "미등록";
    const s = String(v);
    if (s.includes("T")) return s.split("T")[0];
    return s.length >= 10 ? s.slice(0, 10) : s;
  };

  const cpsFeeLabel = (creator) => {
    const fee = Number(creator.manuscriptFee ?? 0);
    const rate = Number(creator.costPerAction ?? 0);
    const pct =
      Number.isFinite(rate) && rate > 0
        ? Math.round(rate * 10000) / 100
        : null;
    const pctStr =
      pct != null ? `${String(pct).replace(/\.00$/, "")}%` : "미정";
    return `₩${fee.toLocaleString()} / ${pctStr}`;
  };

  const creatorCount = data?.creatorCards?.length ?? 0;
  // 닫혀 있을 때 요약용: 검수 상태 건수 (과하지 않게)
  const statusCounts =
    data?.creatorCards?.reduce(
      (acc, c) => {
        (c?.contentStatusList ?? []).forEach((s) => {
          acc[s] = (acc[s] || 0) + 1;
        });
        return acc;
      },
      {}
    ) ?? {};
  const confirmedCount = statusCounts.CONFIRMED ?? 0;
  const reviewCount = statusCounts.REVIEW ?? 0;
  const rejectedCount = statusCounts.REJECTED ?? 0;
  const summaryParts = [];
  if (confirmedCount > 0) summaryParts.push(`검수완료 ${confirmedCount}건`);
  if (reviewCount > 0) summaryParts.push(`검수중 ${reviewCount}건`);
  if (rejectedCount > 0) summaryParts.push(`수정요청 ${rejectedCount}건`);
  const statusSummary = summaryParts.length > 0 ? summaryParts.join(" · ") : null;

  return (
    <Accordion allowMultiple w="100%" defaultIndex={defaultExpanded ? [0] : []}>
      <AccordionItem
        border="1px solid"
        borderColor="rgba(0, 84, 192, 0.14)"
        bg="white"
        borderRadius="12px"
        mb="8px"
        boxShadow="0 1px 3px rgba(23, 41, 64, 0.08)"
        overflow="hidden"
        _expanded={{
          boxShadow: "0 4px 16px rgba(23, 41, 64, 0.12)",
          borderColor: "rgba(0, 84, 192, 0.2)",
        }}
      >
        {({ isExpanded }) => {
          // 아코디언이 완전히 펼쳐진 뒤 렌더하기 위한 상태
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [renderChildren, setRenderChildren] = useState(false);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (isExpanded) {
              const t = setTimeout(() => setRenderChildren(true), 180);
              return () => clearTimeout(t);
            } else {
              setRenderChildren(false);
            }
          }, [isExpanded]);

          return (
            <>
              {/* ================== 아코디언 헤더 (슬림) ================== */}
              <AccordionButton
                py="14px"
                px="18px"
                _hover={{ bg: "gray.50" }}
                _expanded={{ bg: "#F5F9FF" }}
                justifyContent="space-between"
                transition="background 0.2s"
              >
                <Flex
                  align="center"
                  flexDir="row"
                  gap="12px"
                  justify="space-between"
                  w="100%"
                >
                  <Flex flexDir="column" align="flex-start" gap="4px">
                    <Text
                      fontSize="20px"
                      fontWeight="700"
                      color="#172940"
                      noOfLines={1}
                      lineHeight="1.35"
                    >
                      {data?.campaignTitle}
                    </Text>
                    {!isExpanded && (creatorCount > 0 || statusSummary) && (
                      <Text
                        fontSize="14px"
                        color="#5D6979"
                        fontWeight={500}
                        lineHeight="1.4"
                      >
                        {[creatorCount > 0 && `크리에이터 ${creatorCount}명`, statusSummary]
                          .filter(Boolean)
                          .join(" · ")}
                      </Text>
                    )}
                  </Flex>
                  <AccordionIcon
                    fontSize="24px"
                    color="#0054C0"
                    flexShrink={0}
                  />
                </Flex>
              </AccordionButton>

              {/* ================== 아코디언 내부 콘텐츠 (컴팩트) ================== */}
              <AccordionPanel bg="#FAFCFF" px="18px" pt="14px" pb="18px">
                {/* ===== 인플루언서 카드 리스트 ===== */}
                {renderChildren && (
                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing="12px"
                  >
                    {data?.creatorCards?.map((creator) => (
                      <Flex
                        key={creator.creatorProfileId}
                        p="14px"
                        gap="10px"
                        bg="white"
                        borderRadius="10px"
                        border="1px solid"
                        borderColor="gray.200"
                        boxShadow="0 1px 4px rgba(23, 41, 64, 0.08)"
                        flexDir="column"
                      >
                        {/* 크리에이터 정보 */}
                        <Flex
                          flexDir="column"
                          p="8px"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="6px"
                          gap="2px"
                        >
                          <Text
                            color={BRAND.textSecondary}
                            fontSize="14px"
                            fontWeight={500}
                          >
                            크리에이터
                          </Text>

                          <Flex gap="10px" align="center">
                            <Avatar
                              size="sm"
                              w="36px"
                              h="36px"
                              name={creator.username}
                              src={creator.avatarUrl}
                            />
                            <Text fontSize="20px" fontWeight={600}>
                              {creator.username}
                            </Text>

                            {/* 플랫폼 아이콘 */}
                            <Image
                              w="22px"
                              h="22px"
                              src={
                                PLATFORM_ICONS[
                                  creator.platform?.toLowerCase()
                                ] ?? "/images/default.png"
                              }
                              alt="platform-icon"
                            />
                          </Flex>
                        </Flex>

                        {/* 숫자 정보 3개 */}
                        <Flex gap="6px">
                          <InfoBox
                            name="팔로워"
                            value={creator.followers}
                            align
                          />
                          <InfoBox name="게시물" value={creator.posts} align />
                          <InfoBox
                            name="평균 조회수"
                            value={creator.avgViews}
                            align
                          />
                        </Flex>

                        {/* KPI 정보 (CPS는 원고료/커미션율) */}
                        {!isCps && (
                          <Flex gap="6px">
                            <InfoBox
                              name="목표 KPI"
                              value={creator.targetValue ?? "-"}
                            />
                            <InfoBox
                              name="KPI 단가"
                              value={creator.costPerAction ?? "-"}
                            />
                          </Flex>
                        )}
                        {isCps && (
                          <Flex gap="6px">
                            <InfoBox
                              name="원고료 / 커미션율"
                              value={cpsFeeLabel(creator)}
                            />
                          </Flex>
                        )}

                        {/* 제작 일정 */}
                        <InfoBox
                          name="콘텐츠 제작 완료일"
                          value={formatScheduleDate(creator.contentSchedule)}
                        />

                        {/* 콘텐츠 상태 */}
                        <SimpleGrid columns={3} spacing="8px">
                          {creator.contentStatusList?.length > 0 ? (
                            creator.contentStatusList.map((st, idx) => {
                              const status =
                                STATUS_STYLE[st] || STATUS_STYLE.DEFAULT;

                              return (
                                <Flex
                                  key={idx}
                                  w="100%"
                                  flexDir="column"
                                  p="6px"
                                  gap="2px"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  borderRadius="6px"
                                >
                                  <Text
                                    color="#5D6979"
                                    fontSize="14px"
                                    fontWeight={500}
                                  >
                                    {idx + 1}회차
                                  </Text>

                                  <Flex
                                    p="4px"
                                    borderRadius="4px"
                                    fontSize="14px"
                                    fontWeight={600}
                                    justify="center"
                                    align="center"
                                    bg={status.bg}
                                    color={status.color}
                                  >
                                    {status.text}
                                  </Flex>
                                </Flex>
                              );
                            })
                          ) : (
                            <Flex
                              w="100%"
                              flexDir="column"
                              p="6px"
                              gap="2px"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="6px"
                            >
                              <Text
                                color="#5D6979"
                                fontSize="14px"
                                fontWeight={500}
                              >
                                1회차
                              </Text>
                              <Flex
                                p="4px"
                                bg={STATUS_STYLE.DEFAULT.bg}
                                color={STATUS_STYLE.DEFAULT.color}
                                borderRadius="4px"
                                fontSize="14px"
                                fontWeight={600}
                                justify="center"
                                align="center"
                              >
                                {STATUS_STYLE.DEFAULT.text}
                              </Flex>
                            </Flex>
                          )}
                        </SimpleGrid>

                        <Button
                          size="sm"
                          fontSize="18px"
                          fontWeight={600}
                          onClick={() =>
                            navigate(`/contents/detail/${data.proposalId}`)
                          }
                        >
                          상세보기
                        </Button>
                      </Flex>
                    ))}
                  </SimpleGrid>
                )}
              </AccordionPanel>
            </>
          );
        }}
      </AccordionItem>
    </Accordion>
  );
}

function InfoBox({ name, value, align = false }) {
  return (
    <Flex
      w="100%"
      flexDir="column"
      p="6px"
      gap="0"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="6px"
      justifyContent={align ? "center" : "flex-start"}
      alignItems={align ? "center" : "flex-start"}
      textAlign={align ? "center" : "left"}
    >
      <Text color="#5D6979" fontSize="14px" fontWeight={500}>
        {name}
      </Text>
      <Text fontSize="18px" fontWeight={600}>
        {value}
      </Text>
    </Flex>
  );
}
